/**
 * featurebase/SupportButton.tsx
 * Header icon button that opens a support menu with Featurebase Messenger
 * and Feedback Widget options, gated by consent and readiness state.
 *
 * Place in any authenticated header action row — renders as a `?` icon button
 * matching existing header controls (theme toggle, fullscreen toggle).
 */

// 1. External dependencies
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Headphones, HelpCircle, MessageSquarePlus, Newspaper } from 'lucide-react';

// 2. Workspace packages
import {
  Badge,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Spinner,
  useDisclosure,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Relative imports
import { ConsentDialog } from './ConsentDialog';
import { RETRY_COOLDOWN_MS } from './constants';
import { featurebaseService } from './service';
import { useFeaturebase } from './useFeaturebase';

/** Identifies which support surface the user selected from the dropdown. */
type PendingAction = 'support' | 'feedback';

/** Dropdown menu item descriptor for dynamic rendering. */
interface MenuItem {
  key: string;
  label: string;
  icon: ReactNode;
  onPress: () => void;
}

/**
 * SupportButton — Header icon button for Featurebase support.
 *
 * State-based behavior:
 * - granted + ready → dropdown with "Open Support" and "Offer Feedback"
 * - unset / denied → dropdown opens, selecting an item triggers consent dialog
 *   with auto-resume after consent is granted
 * - granted + loading / unloaded → disabled spinner (no dropdown)
 * - granted + error → retries initialization (no dropdown)
 */
export function SupportButton() {
  const { consent, readiness, feedbackReady, retryInitialization, unreadChangelogCount, openChangelogPopup } = useFeaturebase();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const { showToast } = useToastNotification();

  // ----- Pending action state -----
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  // ----- Retry failure detection -----
  const prevReadinessRef = useRef(readiness);
  const retryInFlightRef = useRef(false);

  useEffect(() => {
    const prev = prevReadinessRef.current;
    prevReadinessRef.current = readiness;

    // Detect dispatched retry that failed: loading → error
    if (retryInFlightRef.current && prev === 'loading' && readiness === 'error') {
      showToast({
        title: 'Support Unavailable',
        description: 'Please try again later.',
        color: 'danger',
        timeout: 5000,
      });
    }

    // Clear retry flag on any settled state (not loading)
    if (readiness !== 'loading') {
      retryInFlightRef.current = false;
    }
  }, [readiness, showToast]);

  // ----- Auto-resume effect -----
  useEffect(() => {
    if (!pendingAction || consent !== 'granted') return;

    if (readiness === 'error') {
      const title = pendingAction === 'feedback' ? 'Feedback Unavailable' : 'Support Unavailable';
      setPendingAction(null);
      showToast({
        title,
        description: 'Unable to connect. Please try again later.',
        color: 'danger',
        timeout: 5000,
      });
      return;
    }

    if (readiness === 'ready') {
      if (pendingAction === 'support') {
        featurebaseService.openMessenger();
        setPendingAction(null);
      } else if (pendingAction === 'feedback') {
        if (feedbackReady) {
          featurebaseService.openFeedbackWidget(true);
          setPendingAction(null);
        }
        // else: waiting for feedbackReady — timeout handles failure
      }
    }
  }, [consent, readiness, feedbackReady, pendingAction, showToast]);

  // ----- Dialog dismiss cleanup -----
  useEffect(() => {
    if (!isOpen && pendingAction && consent !== 'granted') {
      setPendingAction(null);
    }
  }, [isOpen, pendingAction, consent]);

  // ----- Pending action timeout -----
  useEffect(() => {
    if (!pendingAction || consent !== 'granted') return;

    const title = pendingAction === 'feedback' ? 'Feedback Unavailable' : 'Support Unavailable';
    const timerId = setTimeout(() => {
      setPendingAction(null);
      showToast({
        title,
        description: 'Please try again later.',
        color: 'danger',
        timeout: 5000,
      });
    }, RETRY_COOLDOWN_MS);

    return () => clearTimeout(timerId);
  }, [pendingAction, consent, showToast]);

  // ----- Menu action handler -----
  const handleMenuAction = useCallback((action: PendingAction) => {
    if (consent === 'unset' || consent === 'denied') {
      setPendingAction(action);
      onOpen();
      return;
    }

    // consent === 'granted' && readiness === 'ready' (only active dropdown state)
    if (action === 'support') {
      featurebaseService.openMessenger();
    } else {
      const opened = featurebaseService.openFeedbackWidget(feedbackReady);
      if (!opened) {
        showToast({
          title: 'Feedback Unavailable',
          description: 'Feedback is currently unavailable — please try again later.',
          color: 'danger',
          timeout: 5000,
        });
      }
    }
  }, [consent, feedbackReady, onOpen, showToast]);

  // ----- Retry handler (error state only) -----
  const handleRetryPress = useCallback(() => {
    const dispatched = retryInitialization();
    if (dispatched) {
      retryInFlightRef.current = true;
    }
  }, [retryInitialization]);

  // ----- Derived state -----
  const isLoading = consent === 'granted' && (readiness === 'loading' || readiness === 'unloaded');
  const isError = consent === 'granted' && readiness === 'error';
  const showBadge = readiness === 'ready' && unreadChangelogCount > 0;
  const badgeContent = unreadChangelogCount > 99 ? '99+' : String(unreadChangelogCount);
  const supportAriaLabel = showBadge
    ? `Support – ${unreadChangelogCount} unread update${unreadChangelogCount === 1 ? '' : 's'}`
    : 'Support';

  // ----- Dynamic menu items -----
  const menuItems = useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [
      { key: 'support', label: 'Open Support', icon: <Headphones size={16} />, onPress: () => handleMenuAction('support') },
      { key: 'feedback', label: 'Offer Feedback', icon: <MessageSquarePlus size={16} />, onPress: () => handleMenuAction('feedback') },
    ];
    if (readiness === 'ready') {
      items.push({ key: 'changelog', label: 'View Changelog', icon: <Newspaper size={16} />, onPress: openChangelogPopup });
    }
    return items;
  }, [readiness, handleMenuAction, openChangelogPopup]);

  return (
    <>
      {isLoading ? (
        <Button
          variant="light"
          color="primary"
          size="sm"
          isIconOnly
          className="shrink-0"
          aria-label="Support"
          isDisabled
        >
          <Spinner size="sm" />
        </Button>
      ) : isError ? (
        <Button
          variant="light"
          color="warning"
          size="sm"
          isIconOnly
          className="shrink-0"
          aria-label="Support — retry"
          onPress={handleRetryPress}
        >
          <HelpCircle size={24} />
        </Button>
      ) : (
        <Badge 
          content={badgeContent} 
          variant="notification" 
          size="sm" 
          isInvisible={!showBadge} 
          classNames={{ base: "border-none!" }}>
          <Dropdown>
            <DropdownTrigger>
              <Button
                variant="light"
                color="primary"
                size="sm"
                isIconOnly
                className="shrink-0"
                aria-label={supportAriaLabel}
              >
                <HelpCircle size={24} />
              </Button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Support options" items={menuItems}>
              {(item) => (
                <DropdownItem key={item.key} startContent={item.icon} onPress={item.onPress}>
                  {item.label}
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        </Badge>
      )}
      <ConsentDialog isOpen={isOpen} onOpenChange={onOpenChange} />
    </>
  );
}
